import { memo, useRef, useState, useEffect } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';

import Draggable from 'react-draggable';

import { CalculatorKeypad, CalculatorDisplay, CalculatorHeader } from './components';

import { useCalculatorHandlers } from './hooks';

import { BASIC_KEYPAD, SCIENTIFIC_KEYPAD, CONVERTER_KEYPAD } from './constants';

import { useRoomContext } from '@src/contexts';

import styles from './calculator.module.scss';

type TProps = {
  isReadOnly?: boolean;
  isShared?: boolean;
  hideHeader?: boolean; // When true, hides CalculatorHeader (useful when header is in drawer)
};

const Calculator = ({ isReadOnly = false, isShared = false, hideHeader = false }: TProps) => {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const mobileSharedNodeRef = useRef<HTMLDivElement | null>(null);
  const [draggableKey, setDraggableKey] = useState(0);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDraggableKey((prev) => prev + 1);
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const { isMobileDevice } = useRoomContext();

  const {
    mode,
    error,
    display,
    operationText,
    isTransitioning,
    sharedCalculatorData,
    isPersonalCalculatorVisible,
    handleClear,
    handleEquals,
    handleNumber,
    handlePercent,
    handleDecimal,
    handleOperator,
    handleBackspace,
    handleModeChange,
    setIsTransitioning,
  } = useCalculatorHandlers({ isReadOnly });

  if (isReadOnly && sharedCalculatorData.isSharedByMe) return null;

  if (!isPersonalCalculatorVisible && !sharedCalculatorData.isOpen) return null;

  if (isMobileDevice && isShared) {
    const calculatorWidth = 242;
    const initialX = typeof window !== 'undefined' ? window.innerWidth / 2 - calculatorWidth / 2 : 0;

    return (
      <motion.div
        ref={mobileSharedNodeRef}
        initial={{ opacity: 0, scale: 0.9, x: initialX, y: 20 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{ zIndex: 1500, position: 'fixed' }}
        drag
        dragConstraints={
          typeof window !== 'undefined'
            ? {
                left: 10,
                right: window.innerWidth - calculatorWidth - 10,
                top: 10,
                bottom: window.innerHeight - 140 - 10,
              }
            : {}
        }
        dragElastic={0.2}
        whileDrag={{ scale: 1.05 }}
      >
        <div className={classNames(styles.wrapper, styles.calculatorContainer, styles.sharedCalculatorMobile)}>
          <CalculatorHeader mode={mode} isReadOnly={true} onModeChange={handleModeChange} />
          <CalculatorDisplay isReadOnly={true} value={display} operation={operationText} error={error} mode={mode} />
          {isTransitioning && <div className={styles.transitionOverlay} />}
        </div>
      </motion.div>
    );
  }

  const isPersonalMobile = isMobileDevice && !isShared && !isReadOnly;
  const wrapperClass = isPersonalMobile ? styles.wrapperFullScreen : styles.wrapper;
  const baseContainerClass = isPersonalMobile ? styles.calculatorContainerFullScreen : styles.calculatorContainer;
  const containerClass = classNames(baseContainerClass, styles[mode]);

  const calculatorContent = (
    <div ref={nodeRef} className={wrapperClass}>
      {/* Calculator Body */}
      <div className={containerClass}>
        {!hideHeader && <CalculatorHeader mode={mode} isReadOnly={isReadOnly} onModeChange={handleModeChange} />}

        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            onAnimationComplete={() => setIsTransitioning(false)}
            className={styles.motionDiv}
          >
            <CalculatorDisplay
              isReadOnly={isReadOnly}
              value={display}
              operation={operationText}
              error={error}
              mode={mode}
            />

            <CalculatorKeypad
              layout={mode === 'basic' ? BASIC_KEYPAD : mode === 'scientific' ? SCIENTIFIC_KEYPAD : CONVERTER_KEYPAD}
              mode={mode}
              isReadOnly={isReadOnly}
              operations={{
                handleNumber,
                handleOperator,
                handleClear,
                handleEquals,
                handleDecimal,
                handlePercent,
                handleBackspace,
              }}
            />
          </motion.div>
        </AnimatePresence>

        {isTransitioning && <div className={styles.transitionOverlay} />}
      </div>
    </div>
  );

  if (isPersonalMobile) {
    return calculatorContent;
  }

  return (
    <Draggable key={draggableKey} nodeRef={nodeRef} handle={`.${styles.dragHeader}`} bounds="parent">
      {calculatorContent}
    </Draggable>
  );
};

export default memo(Calculator);
